import {
    Box,
    Button,
    Flex,
    IconButton,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverHeader,
    PopoverTrigger,
    Text,
    VStack
} from '@chakra-ui/react'
import { FiBell } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { useNotifications } from '@/shared/hooks/useNotifications'
import { NOTIFICATION_STYLES } from './notificationTheme'

const MAX_VISIBLE = 6

/** Header bell with an unread count, backed by the derived notification feed. */
export default function NotificationBell() {
    const {
        notifications,
        otherBranchCount,
        unreadCount,
        markRead,
        markAllRead,
        hasFeed
    } = useNotifications()
    const navigate = useNavigate()

    // Admin teachers always have a feed; everyone else only once something is
    // actually waiting for them (today: a suspected duplicate account). The bell
    // stays hidden otherwise rather than showing a permanently empty popover.
    if (!hasFeed) return null

    const visible = notifications.slice(0, MAX_VISIBLE)

    return (
        <Popover placement="bottom-end">
            <PopoverTrigger>
                <Box position="relative" display="inline-flex">
                    <IconButton
                        aria-label={
                            unreadCount > 0
                                ? `Notifications, ${unreadCount} unread`
                                : 'Notifications'
                        }
                        icon={<FiBell />}
                        variant="ghost"
                        fontSize="xl"
                    />
                    {unreadCount > 0 ? (
                        <Flex
                            position="absolute"
                            top="0"
                            right="0"
                            minW="18px"
                            h="18px"
                            px="1"
                            align="center"
                            justify="center"
                            bg="red.500"
                            color="white"
                            borderRadius="full"
                            fontSize="10px"
                            fontWeight="bold"
                            pointerEvents="none"
                        >
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </Flex>
                    ) : null}
                </Box>
            </PopoverTrigger>
            <PopoverContent w={{ base: '320px', md: '380px' }}>
                <PopoverArrow />
                <PopoverHeader>
                    <Flex align="center" justify="space-between" gap={2}>
                        <Text fontWeight="semibold" fontSize="sm">
                            Notifications
                        </Text>
                        {unreadCount > 0 ? (
                            <Button size="xs" variant="link" onClick={markAllRead}>
                                Mark all read
                            </Button>
                        ) : null}
                    </Flex>
                </PopoverHeader>
                <PopoverBody px={0} py={0} maxH="360px" overflowY="auto">
                    {visible.length === 0 ? (
                        <Text p={4} fontSize="sm" color="gray.500">
                            {otherBranchCount > 0
                                ? `No requests for your branch. ${otherBranchCount} awaiting approval elsewhere.`
                                : "You're all caught up."}
                        </Text>
                    ) : (
                        <VStack align="stretch" spacing={0}>
                            {visible.map((notification) => {
                                // Same accent/tint the dashboard feed uses, so a
                                // teacher request and a duplicate account are
                                // told apart in the bell too.
                                const style = NOTIFICATION_STYLES[notification.type]
                                return (
                                    <Box
                                        as="button"
                                        key={notification.id}
                                        textAlign="left"
                                        px={4}
                                        py={3}
                                        borderBottomWidth="1px"
                                        borderColor="gray.50"
                                        borderLeftWidth="3px"
                                        borderLeftColor={
                                            notification.isRead
                                                ? 'gray.200'
                                                : style.accent
                                        }
                                        bg={notification.isRead ? 'white' : style.tint}
                                        _hover={{
                                            bg: notification.isRead
                                                ? 'gray.50'
                                                : style.tint
                                        }}
                                        onClick={() => {
                                            markRead(notification.id)
                                            navigate(notification.href)
                                        }}
                                    >
                                        <Text
                                            fontSize="sm"
                                            fontWeight="medium"
                                            noOfLines={2}
                                        >
                                            {notification.title}
                                        </Text>
                                        <Text
                                            fontSize="xs"
                                            color="gray.500"
                                            noOfLines={1}
                                        >
                                            {notification.description}
                                        </Text>
                                    </Box>
                                )
                            })}
                        </VStack>
                    )}
                </PopoverBody>
            </PopoverContent>
        </Popover>
    )
}
